<template>
    <div id="main">
        <div class="alert-wrapper">
            <alert-container :messages="msgs" />
        </div>

        <!-- Título -->
        <div class="atendimento">
            <h1 class="title">{{$t('comparativo-de-vendas')}}</h1>
            <nav class="breadcrumb" aria-label="breadcrumbs">
                <ul>
                    <li><a>{{$t('vendas')}}</a></li>
                    <li><a>{{$t('relatorios')}}</a></li>
                    <li><a href="#" aria-current="page">{{$t('comparativo-de-vendas')}}</a></li>
                </ul>
            </nav>
        </div>

        <!-- Filtros -->
        <div class="page page-container filtros-main">
            <div class="column is-4">
                <label v-text="$t('tipo-analise')"/>
                <vss-select :options="tiposAnalises" v-model="tipoAnalise" ml-labels>
                </vss-select>
            </div>
            <div class="column is-4">
                <label v-text="$t('referencia')"/>
                <the-mask
                    class="the-mask"
                    ref="refInput"
                    mask="##/####"
                    :masked="true"
                    :placeholder="$t('periodo-placeholder')"
                    v-model="ref"
                />
            </div>
            <div class="column is-4">
                <label v-text="$t('situacao')"/>
                <v-select
                    ref="situacaoInput"
                    :clearable="false"
                    v-model="situacao"
                    :options="situacoes"
                >
                    <template slot="option" slot-scope="option">
                        {{ $t(option.label) }}
                    </template>
                    <template slot="selected-option" slot-scope="option">
                        {{ $t(option.label) }}
                    </template>
                    <template slot="no-options">{{$t('sem-resultados')}}</template>
                </v-select>
            </div>
            <div class="column is-6">
                <label v-text="$t('sellers')"/>
                <v-select
                    ref="vendedorInput"
                    :clearable="false"
                    v-model="vendedoresSelected"
                    :options="vendedoresOptions"
                    :multiple="true"
                    :loading="vendedoresLoading"
                    @search="loadVendedores"
                >
                    <template slot="no-options">{{ $t('sem-resultados-filtro') }}</template>
                </v-select>
            </div>
            <div class="column is-6">
                <vss-select
                    title="tipo-pedido"
                    placeholder="selecione"
                    :options="tipoPedidoList"
                    v-model="tipoPedidoSelected"
                    :loading="tipoPedidoLoading"
                    @search="carregarTipoPedido"
                    multiple
                    searchable
                    clearable
                    no-clearfix
                    :closeOnSelect="false"
                >
                    <template #multiple-selected="{ selected }">
                        {{$t('selecionado')}} ({{  selected && selected.length > 0 ? selected.length : $t('todos') }})
                    </template>
                </vss-select>
            </div>
            <div class="control-buttons mt-5">
                <div class="ctrl">
                    <button class="delete-btn pl-4 pr-4" type="button" @click="limparFiltros">
                        {{ $t('limpar-filtros') }}
                    </button>
                </div>
                <div class="ctrl">
                    <button class="save-btn pl-4 pr-4" type="button" @click="getDados">
                        {{ $t('pesquisar') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="chart-content mt-4" v-if="reportData.length > 0 && !isSearching">
            <chart-comparativo-vendas
                :data="reportData"
                :referencia="refPesq"
            />
        </div>

        <!-- Tabela -->
        <div class="page results" v-if="reportData.length > 0 && !isSearching">
            <b-table
                :data="reportData"
                :paginated="true"
                :per-page="perPage"
                :current-page.sync="currentPage"
                class="data-table"
            >
                    <b-table-column v-for="(f, i) in tableFields"
                        :key="i"
                        :field="f.key"
                        :label="f.key != 'referencia' ? $t(f.label) : `${$t(f.label)} (${refPesq})`" 
                        :sortable="f.sortable"
                        :width="f.width"
                        :numeric="f.numeric"
                        v-slot="props"
                    >
                        <div v-if="f.key != 'nome'">
                            R$ {{ props.row[f.key].toLocaleString(getLocale(), localeOpts) }}
                        </div>
                        <div v-else>
                            {{ props.row[f.key] }}
                        </div>
                    </b-table-column>
            </b-table>
        </div>
        
        <!-- Loader -->
        <div
            v-show="isSearching"
            class="page searching-loader"
        >
            <svg class="loading">
                <use :xlink:href="require('@/assets/img/icons/loader.svg')+'#layer'"></use>
            </svg>
            <div>
                {{ $t('carregando-dados') }}...
            </div>
        </div>

        <!-- Mensagem 'não há dados' e ajuda de utilização -->
        <div
            v-show="(firstTime || noData) && !isSearching"
            class="page searching-loader"
        >
            <svg class="bigicon">
                <use v-if="firstTime" :xlink:href="require('@/assets/img/icons/magnify.svg')+'#layer'"></use>
                <use v-else :xlink:href="require('@/assets/img/icons/chart-error.svg')+'#layer'"></use>
            </svg>
            <div v-if="firstTime">
                {{ $t('descricao-ajuda-filtro-relatorio') }}
            </div>
            <div v-else>
                {{ $t('no-data') }}
            </div>
        </div>
    </div>
</template>

<script>
import ChartComparativoVendas from "./ChartComparativoVendas";
import FiltroRelatorioService from "@/services/FiltroRelatorioService";
import VendasRelComparativoService from "@/services/VendasRelComparativoService";
import TipoPedidoService from "@/services/vendas/TipoPedidoService.js";
import Message from "@/models/Message";
import ColaboradorService from "@/services/ColaboradorService";

export default {
    components: {
        ChartComparativoVendas
    },
    data() {
        return {
            filtroService: null,
            tipoPedidoService: new TipoPedidoService(this.$http, this.$router),
            dataService: null,
            ref: '',
            refPesq: '',
            situacoes: [ {id: 0, label: 'todos'}, {id: 1, label: 'superada'}, {id: 2, label: 'nao-superada'}],
            situacao: {id: 1, label: 'superada'},
            vendedoresOptions: [],
            vendedoresSelected: null,
            vendedoresLoading: false,
            tableFields: [
                {key: 'nome',       label: 'nome',                  sortable: true},
                {key: 'media',      label: 'media-ultimos-3-meses', sortable: true, width: 220, numeric: true},
                {key: 'referencia', label: 'referencia',            sortable: true, width: 200, numeric: true}
            ],
            perPage: 20,
            currentPage: 1,
            reportData: [],
            localeOpts: {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            },
            firstTime: true,
            noData: false,
            isSearching: false,
            msgs: [],
            tiposAnalises: [{label: 'pedido'}, {label: 'faturamento'}],
            tipoAnalise: {label: 'pedido'},
            permissao: {},
            tipoPedidoList: [],
            tipoPedidoSelected: [],
            tipoPedidoLoading: false,
        };
    },
    created () {
        this.permissao = this.hasPermissionAccess('vendas', 'relatorio-analitico', 'relatorio-vendas-comparativo-vendas');

        !this.permissao.read && this.$router.push({
            name: 'acesso-nao-permitido',
            params: { tela: 'comparativo-de-vendas' },
        });

        let d = new Date();
        
        this.filtroService = new FiltroRelatorioService(this.$http, this.$router);
        this.dataService = new VendasRelComparativoService(this.$http, this.$router);
        this.colaboradorService = new ColaboradorService(this.$http, this.$router);
        this.loadVendedores('');
        this.ref = this.getPeriodoStringFromDate(d);
    },
    mounted () {  
    },
    methods: {
        showMessage (type, message) {
            this.msgs.push(
                new Message(type, message, true)
            );
        },
        loadVendedores(q) {
                this.vendedoresLoading = true;
                this.colaboradorService.findListAssociated(q)
                    .then(r => {
                        this.vendedoresOptions = r.content.map(v => {
                            return {
                                id: v.id,
                                label: v.nome.toLocaleUpperCase()
                            }
                        });
                    })
                    .catch(e => {
                        console.error('Erro ao carregar vendedores.', e);
                    })
                    .finally(_ => {
                        this.vendedoresLoading = false;  
                });
        },
        getPeriodoStringFromDate (d) {
            return `${d.getMonth() < 9 ? '0' : ''}${d.getMonth() + 1}/${d.getFullYear()}`;
        },
        limparFiltros () {
            console.log('Limpando os filtros.');
            this.vendedoresSelected = [];
            this.tipoPedidoSelected = [];
        },
        getDados () {
            console.log('Pesquisando dados de comparativo de vendas.');
            if (this.validateDatas()) {
                let filtro = {};

                this.isSearching = true;

                if (this.firstTime) {
                    this.firstTime = false;
                }  
    
                filtro.referencia = this.ref;
                filtro.superado = this.situacao.id;
                filtro.vendedores = this.vendedoresSelected != null ? this.vendedoresSelected.map(v => v.id) : [];
                filtro.tiposPedido = this.tipoPedidoSelected.map(v => v.id);
                filtro.tipoAnalise = this.tipoAnalise != null ? this.tipoAnalise.label : 'pedido';
    
                console.log('Filtro válido', filtro);
                this.dataService.load(filtro)
                    .then(r => {
                        if (r.status != 200) {
                            this.showMessage('error', 'msg-erro-relatorio');
                            this.reportData = [];
                            this.noData = true;
                        } else {
                            this.noData = r.content.length == 0;
                            this.reportData = r.content;
                            this.refPesq = this.ref;
                            console.log('Dados de comparativo de vendas carregado.')
                        }
                    })
                    .catch(e => {
                        console.error('Erro ao carregar dados de comparativos de vendas.', e);
                        this.showMessage('error', 'msg-erro-relatorio');
                        this.reportData = [];
                        this.noData = true;
                    })
                    .finally(() => {
                        this.isSearching = false;
                    });
            }
        },
        validateDatas() {
            let dr = /\d\d\/\d\d\d\d/; // regex para dd/dddd
            let valid = true;
            let dsplit = null;

            /* Validando período de início */
            if (!dr.test(this.ref)) {
                console.error(`Validação falhou no período de referência, data inválida: [${this.ref}]`);
                this.showMessage('error', 'msg-erro-periodo-referencia');
                valid = false;
            } else {
                dsplit = this.ref.split('/');

                dsplit[0] = parseInt(dsplit[0]); // Mês
                dsplit[1] = parseInt(dsplit[1]); // Ano
            }

            if (dsplit !== null) {
                if ((dsplit[0] < 1 && dsplit[0] > 12) || (dsplit[1] < 1900 || dsplit[1] > 2200)) {
                    console.error(`Validação falhou no período de referência, data inválida: [${this.ref}]`);
                    this.showMessage('error', 'msg-erro-periodo-referencia');
                    valid = false;
                }
            }

            return valid;
        },
        carregarTipoPedido(query = null) {
            this.tipoPedidoLoading = true;
            query = this.isNull(query) ? ".*" : this.removeToSearch(query);

            this.tipoPedidoService
                .findAllCombo(query)
                .then(this.mapTipoPedidoList)
                .then((value) => {
                    if (value != null) {
                        this.tipoPedidoList = value;
                    }
                })
                .catch((e) => {
                    this.$root.showMessage("error", "msg-erro-load", true);
                })
                .finally((_) => {
                    this.tipoPedidoLoading = false;
                });
        },
        mapTipoPedidoList(response) {
            return response.content.map((m) => {
                return {
                    id: m.id,
                    label: m.descricao,
                };
            });
        },
    }
};
</script>

<style lang="scss" scoped>
@import "../../../../mixins.scss";

$padding: 5px;

#main {
    padding: 0 15px 15px 20px;
    height: auto;
    font-size: 13px;
}

.page {
    position: relative;

    .the-mask{
        height: 37px;
    }

    &.filtros-main {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
    }

    .control-buttons {
        display: flex;
        align-items: flex-end;
        justify-content: space-around;
        width: 100%;
        height: 30px;
    }

    &.results {
        padding-top: 1rem;
        margin-top: 15px;

        .data-table {
            padding: 0;
        }
    }

}

.chart-content {
    background-color: $sovis-white;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    padding: 0.6rem 1rem 1rem 1rem;
}

.control-buttons {
    margin: 0;
}

.searching-loader {
    margin-top: 20px;
}
</style>